import { logger } from '../utils/logger';
import { supabase } from './supabaseService';
import type { ProbabilityResult, RiskLevel, ConfidenceLevel } from '../types/leadIntelligence';

const PROBABILITY_VERSION = 'lead_probability_v1';

interface LeadProbabilityInput {
  id: string;
  leadTier?: string;
  projectLine?: string;
  painLevel?: string;
  leadRedFlags?: string[];
  leadPainSignals?: string[];
  leadFitSignals?: string[];
  leadScoreBreakdown?: {
    pain: number;
    fit: number;
    budget: number;
    seriousness: number;
  };
  notes?: string;
  productInterest?: string;
}

function countWords(text: string): number {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(w => w.length > 0).length;
}

function hasBudgetSignal(flags: string[], signals: string[], notes: string, productInterest: string): 'clear' | 'range' | 'none' | 'low' {
  const allText = [notes, productInterest, ...flags, ...signals].join(' ').toLowerCase();

  const lowBudgetPatterns = [
    'bajo presupuesto', 'low budget', 'poco presupuesto', 'presupuesto ajustado',
    'very low', 'muy bajo', 'sin presupuesto', 'no budget', 'gratis', 'free',
    'barato', 'cheap', 'económico', 'pocos recursos'
  ];
  if (lowBudgetPatterns.some(p => allText.includes(p))) return 'low';

  const clearBudgetPatterns = [
    'presupuesto de', 'budget of', 'tenemos €', 'tenemos $', 'contamos con €',
    'contamos con $', 'disponemos de €', 'disponemos de $', 'inversión de',
    'investment of', 'budget:', 'presupuesto:', 'we have a budget'
  ];
  if (clearBudgetPatterns.some(p => allText.includes(p))) return 'clear';

  const rangeBudgetPatterns = [
    'entre €', 'entre $', 'around €', 'around $', 'roughly €', 'roughly $',
    'approximately', 'aproximadamente', 'rango de', 'range of',
    'unos €', 'unos $', 'about €', 'about $', 'k€', 'k$',
    'miles de euros', 'thousand', 'k euros', 'k dollars'
  ];
  if (rangeBudgetPatterns.some(p => allText.includes(p))) return 'range';

  const noMoneyPatterns = [
    'no menciona dinero', 'no budget mentioned', 'sin mención de presupuesto',
    'no habla de precio', 'no price mentioned'
  ];
  if (noMoneyPatterns.some(p => allText.includes(p))) return 'none';

  const hasMoneySignal = /\d+[\s]?[k€$£]|€[\s]?\d|$[\s]?\d|\d+\.?\d*[\s]?(euro|dollar|usd|eur)/i.test(allText);
  if (hasMoneySignal) return 'range';

  return 'none';
}

function hasSignal(signals: string[], patterns: string[]): boolean {
  const joined = signals.join(' ').toLowerCase();
  return patterns.some(p => joined.includes(p.toLowerCase()));
}

function hasTextSignal(text: string, patterns: string[]): boolean {
  const lower = text.toLowerCase();
  return patterns.some(p => lower.includes(p.toLowerCase()));
}

export function calculateProbabilities(lead: LeadProbabilityInput): ProbabilityResult {
  const tier = lead.leadTier || 'COLD';
  const projectLine = lead.projectLine || '';
  const painLevelText = lead.painLevel || 'low';
  const redFlags = lead.leadRedFlags || [];
  const painSignals = lead.leadPainSignals || [];
  const fitSignals = lead.leadFitSignals || [];
  const breakdown = lead.leadScoreBreakdown;
  const notes = lead.notes || '';
  const productInterest = lead.productInterest || '';
  const allText = [notes, productInterest].join(' ');

  const riskReasons: string[] = [];

  // 1. Base by tier
  let closeBase = tier === 'HOT' ? 60 : tier === 'WARM' ? 35 : 10;
  let proposalBase = tier === 'HOT' ? 80 : tier === 'WARM' ? 55 : 20;

  // 2. Project line adjustments
  let closeAdj = 0;
  let proposalAdj = 0;

  if (projectLine === 'A_CORE_SYSTEM') {
    closeAdj += 15;
    proposalAdj += 10;
  } else if (projectLine === 'B_BIG_PLATFORM') {
    closeAdj -= 5;
    proposalAdj += 5;
  } else if (projectLine === 'C_RESCUE_IMPROVEMENTS') {
    closeAdj += 8;
    proposalAdj += 6;
  }

  // 3. Pain level adjustments (map text → numeric)
  const painMap: Record<string, number> = { critical: 5, high: 4, medium: 3, low: 2 };
  const painNumeric = painMap[painLevelText] ?? 2;

  if (painNumeric === 5) { closeAdj += 12; proposalAdj += 10; }
  else if (painNumeric === 4) { closeAdj += 10; proposalAdj += 8; }
  else if (painNumeric === 3) { closeAdj += 5; proposalAdj += 4; }
  else if (painNumeric === 2) { closeAdj += 0; proposalAdj += 0; }
  else { closeAdj -= 5; proposalAdj -= 3; }

  // 4. Positive signals from pain/fit signal lists
  const operationalPatterns = ['operativa', 'operational', 'real company', 'empresa real', 'active business', 'empresa operativa'];
  if (hasSignal([...painSignals, ...fitSignals], operationalPatterns)) {
    closeAdj += 8;
    proposalAdj += 6;
  }

  const brokenProcessPatterns = ['proceso roto', 'broken process', 'proceso actual', 'current process', 'problema de proceso', 'process problem', 'ineficiencia', 'inefficiency'];
  if (hasSignal(painSignals, brokenProcessPatterns)) {
    closeAdj += 10;
    proposalAdj += 6;
  }

  const integrationPatterns = ['integración', 'integration', 'api', 'sistema existente', 'existing system', 'conectar con', 'connect with'];
  if (hasSignal([...painSignals, ...fitSignals], integrationPatterns)) {
    closeAdj += 6;
    proposalAdj += 5;
  }

  const detailedLeadPatterns = ['muy detallado', 'detailed', 'completo', 'comprehensive', 'thorough', 'especificaciones', 'specifications'];
  const wordCount = countWords(notes) + countWords(productInterest);
  if (wordCount > 300 || hasSignal([...painSignals, ...fitSignals], detailedLeadPatterns)) {
    closeAdj += 6;
    proposalAdj += 5;
  }

  const teamStructurePatterns = ['equipo', 'team', 'estructura', 'structure', 'departamento', 'department', 'organización', 'organization'];
  if (hasSignal([...painSignals, ...fitSignals], teamStructurePatterns)) {
    closeAdj += 4;
    proposalAdj += 3;
  }

  const deadlinePatterns = ['deadline', 'fecha límite', 'para el', 'by', 'plazo', 'necesitamos en', 'need by', 'lanzar en'];
  if (hasSignal([...painSignals, ...fitSignals], deadlinePatterns) || hasTextSignal(allText, deadlinePatterns)) {
    closeAdj += 5;
    proposalAdj += 4;
  }

  const executivePatterns = ['director', 'ceo', 'founder', 'fundador', 'cto', 'coo', 'gerente', 'manager', 'responsable', 'ejecutivo', 'executive'];
  if (hasSignal([...painSignals, ...fitSignals], executivePatterns) || hasTextSignal(allText, executivePatterns)) {
    closeAdj += 5;
    proposalAdj += 4;
  }

  // 5. Budget signal adjustments
  const budgetSignal = hasBudgetSignal(redFlags, [...painSignals, ...fitSignals], notes, productInterest);
  if (budgetSignal === 'clear') {
    closeAdj += 10;
    proposalAdj += 6;
  } else if (budgetSignal === 'range') {
    closeAdj += 8;
    proposalAdj += 5;
  } else if (budgetSignal === 'none') {
    closeAdj -= 12;
    proposalAdj -= 4;
    riskReasons.push('no clear budget mentioned');
  } else if (budgetSignal === 'low') {
    closeAdj -= 20;
    proposalAdj -= 10;
    riskReasons.push('budget appears too low for scope');
  }

  // Also check breakdown.budget directly
  if (breakdown && breakdown.budget <= 5) {
    closeAdj -= 12;
    proposalAdj -= 4;
    if (!riskReasons.includes('no clear budget mentioned')) {
      riskReasons.push('no clear budget mentioned');
    }
  }

  // 6. Red flag adjustments
  const conceptualPatterns = ['conceptual', 'idea', 'no tiene claro', 'not sure', 'thinking about', 'pensando en', 'solo idea', 'just an idea'];
  if (hasSignal(redFlags, conceptualPatterns) || hasTextSignal(allText, conceptualPatterns)) {
    closeAdj -= 20;
    proposalAdj -= 10;
    riskReasons.push('too conceptual — no concrete problem defined');
  }

  const unrealisticUrgencyPatterns = ['urgente ya', 'immediately', 'asap', 'para mañana', 'by tomorrow', 'esta semana ya', 'urgentísimo', 'very urgent', 'needs to launch yesterday'];
  if (hasSignal(redFlags, unrealisticUrgencyPatterns) || hasTextSignal(allText, unrealisticUrgencyPatterns)) {
    closeAdj -= 8;
    proposalAdj -= 5;
    riskReasons.push('unrealistic urgency signals');
  }

  const uberPatterns = ['uber de', 'uber of', 'airbnb de', 'airbnb of', 'tinder de', 'tinder of', 'spotify de', 'spotify of', 'amazon de', 'amazon of'];
  if (hasSignal(redFlags, uberPatterns) || hasTextSignal(allText, uberPatterns)) {
    closeAdj -= 15;
    proposalAdj -= 10;
    riskReasons.push('copycat platform idea ("Uber of X") — high risk');
  }

  const negotiationPatterns = ['comparando', 'comparing', 'varios presupuestos', 'multiple quotes', 'otras empresas', 'other companies', 'competencia', 'competitor quote', 'pedir precio', 'just checking price'];
  if (hasSignal(redFlags, negotiationPatterns) || hasTextSignal(allText, negotiationPatterns)) {
    closeAdj -= 15;
    proposalAdj -= 8;
    riskReasons.push('likely comparing quotes against competitors');
  }

  const multipleStakeholderPatterns = ['varios decisores', 'multiple stakeholders', 'committee', 'comité', 'board', 'junta', 'hay que consultar', 'need to check with', 'depende de', 'depends on'];
  if (hasSignal(redFlags, multipleStakeholderPatterns) || hasTextSignal(allText, multipleStakeholderPatterns)) {
    closeAdj -= 10;
    proposalAdj -= 5;
    riskReasons.push('multiple stakeholders / unclear decision maker');
  }

  const scopeBudgetMismatchPatterns = ['alcance enorme', 'huge scope', 'sin presupuesto definido', 'no defined budget', 'todo por poco', 'everything for little', 'desajuste', 'mismatch'];
  if (hasSignal(redFlags, scopeBudgetMismatchPatterns)) {
    closeAdj -= 20;
    proposalAdj -= 12;
    riskReasons.push('significant scope vs budget mismatch');
  }

  // 7. Word count adjustments (only if not already counted from detailed signal)
  if (!hasSignal([...painSignals, ...fitSignals], detailedLeadPatterns)) {
    if (wordCount > 300) {
      closeAdj += 6;
      proposalAdj += 5;
    } else if (wordCount >= 150) {
      closeAdj += 3;
      proposalAdj += 2;
    } else {
      closeAdj -= 4;
      proposalAdj -= 2;
    }
  }

  // 8. Cap final scores
  const closeProbability = Math.min(100, Math.max(0, closeBase + closeAdj));
  const proposalProbability = Math.min(100, Math.max(0, proposalBase + proposalAdj));

  // 9. Risk level
  let riskLevel: RiskLevel;
  if (closeProbability >= 65) riskLevel = 'LOW';
  else if (closeProbability >= 35) riskLevel = 'MEDIUM';
  else riskLevel = 'HIGH';

  // 10. Confidence level
  let confidenceLevel: ConfidenceLevel;
  const hasCompany = hasTextSignal(allText, ['empresa', 'company', 'startup', 'negocio', 'business']);
  const hasProblem = painSignals.length >= 2;
  const hasStructure = hasTextSignal(allText, executivePatterns) || hasTextSignal(allText, teamStructurePatterns);
  const informationPieces = [
    wordCount > 150,
    hasProblem,
    hasCompany,
    hasStructure,
    budgetSignal !== 'none',
    projectLine !== ''
  ].filter(Boolean).length;

  if (informationPieces >= 4) confidenceLevel = 'HIGH';
  else if (informationPieces >= 2) confidenceLevel = 'MEDIUM';
  else confidenceLevel = 'LOW';

  // 11. Generate summary
  const probabilitySummary = buildSummary(lead, closeProbability, proposalProbability, riskLevel, confidenceLevel, riskReasons, tier, projectLine, budgetSignal, painNumeric);

  return {
    proposalProbability,
    closeProbability,
    riskLevel,
    riskReasons,
    confidenceLevel,
    probabilitySummary,
    probabilityVersion: PROBABILITY_VERSION
  };
}

function buildSummary(
  lead: LeadProbabilityInput,
  close: number,
  proposal: number,
  risk: RiskLevel,
  confidence: ConfidenceLevel,
  reasons: string[],
  tier: string,
  projectLine: string,
  budgetSignal: string,
  painNumeric: number
): string {
  const parts: string[] = [];

  const tierLabel = tier === 'HOT' ? 'strong' : tier === 'WARM' ? 'moderate' : 'weak';
  parts.push(`${tierLabel.charAt(0).toUpperCase() + tierLabel.slice(1)} lead tier (${tier})`);

  if (projectLine === 'A_CORE_SYSTEM') {
    parts.push('good project line fit for core system development');
  } else if (projectLine === 'B_BIG_PLATFORM') {
    parts.push('big platform project — higher complexity, higher risk of not closing');
  } else if (projectLine === 'C_RESCUE_IMPROVEMENTS') {
    parts.push('rescue or improvement project — typically closes faster');
  }

  if (painNumeric >= 4) {
    parts.push('strong operational pain signals detected');
  } else if (painNumeric === 3) {
    parts.push('moderate pain signals present');
  } else {
    parts.push('weak pain signals — business urgency unclear');
  }

  if (budgetSignal === 'clear') {
    parts.push('budget clearly stated');
  } else if (budgetSignal === 'range') {
    parts.push('budget range mentioned');
  } else if (budgetSignal === 'none') {
    parts.push('no budget information available — reduces closing confidence');
  } else if (budgetSignal === 'low') {
    parts.push('budget appears insufficient for scope');
  }

  if (reasons.length > 0) {
    const topReasons = reasons.slice(0, 2).join('; ');
    parts.push(`key risk factors: ${topReasons}`);
  }

  return parts.join('. ') + '.';
}

export async function runProbabilityAnalysis(leadId: string, leadInput: LeadProbabilityInput): Promise<void> {
  try {
    const result = calculateProbabilities(leadInput);

    const { error } = await supabase
      .from('leads')
      .update({
        proposal_probability: result.proposalProbability,
        close_probability: result.closeProbability,
        risk_level: result.riskLevel,
        risk_reasons: result.riskReasons,
        confidence_level: result.confidenceLevel,
        probability_summary: result.probabilitySummary,
        probability_version: result.probabilityVersion
      })
      .eq('id', leadId);

    if (error) {
      logger.error(`leadProbabilityService: error persisting probability for lead ${leadId}`, error);
      throw error;
    }

    logger.info(`leadProbabilityService: lead ${leadId} — close=${result.closeProbability}% proposal=${result.proposalProbability}% risk=${result.riskLevel} confidence=${result.confidenceLevel}`);
  } catch (error) {
    logger.error(`leadProbabilityService: analysis failed for lead ${leadId}`, error);
  }
}

export const leadProbabilityService = {
  calculateProbabilities,
  runProbabilityAnalysis
};
